// Dashboard widget size model (PRD #214 / issue #215, ADR-0009).
//
// The size-aware flow grid (`WidgetGrid`) gives every pinned widget a width and
// a height in grid cells, so a one-number tile can be 1x1 and a seven-column
// table a full-width 4x3 on the same canvas. This module is the pure,
// unit-tested core of that model: the size SPECS (a default + an ordered preset
// cycle), the resize cycle, the static Tailwind span-class maps the grid
// renders, and the legacy-coercion helper that lets an old `string[]` saved
// layout (PRD #185) still load.
//
// Two layers of spec (KPI widget-sizing follow-up):
//   - a per-KIND fallback (`WIDGET_KIND_SPECS`) — the archetype's shape, and
//   - an optional per-WIDGET override (`LibraryWidget.size`) — e.g. a donut
//     wants a square while a trend line wants a wide rectangle, even though both
//     are `chart`s. `sizeSpecFor` folds the override over the kind fallback.
//
// No inline styles and no dynamically-built class strings: every span class is
// a full literal so Tailwind's JIT scanner keeps it in the build. The maps are
// keyed by the discrete `w`/`h` values so the grid only ever indexes them with
// a value that already exists.

/**
 * The three widget archetypes on a My Page canvas. A `kind` is declared per
 * catalogue entry (`LibraryWidget.kind`) and drives the FALLBACK size spec a
 * widget gets when it declares no per-widget `size`:
 *   - `tile`  — a single headline figure (`MetricTile`); small.
 *   - `chart` — a bars / trend / donut chart; medium, can grow wide.
 *   - `table` — a multi-column table (league / categories); always full-width.
 */
export type WidgetKind = 'tile' | 'chart' | 'table';

/** Width in grid columns (the canvas is 4 columns wide at `lg`). */
export type WidgetWidth = 1 | 2 | 4;
/** Height in grid rows (each row is `auto-rows-widget-cell` = 120px). */
export type WidgetHeight = 1 | 2 | 3 | 4;

/** A widget's footprint on the flow grid, in grid cells. */
export interface WidgetSize {
    w: WidgetWidth;
    h: WidgetHeight;
}

/**
 * A widget's sizing rule: the default footprint it gets when none is stored,
 * plus the ordered set of presets the resize control steps through. The first
 * preset is — by convention, asserted in tests — the default.
 */
export interface WidgetSizeSpec {
    default: WidgetSize;
    presets: ReadonlyArray<WidgetSize>;
}

/**
 * Per-KIND fallback specs. A widget with no per-widget `size` override uses the
 * spec for its kind. Tables are always full-width (4 wide) and tall enough to
 * show several rows; tiles are small (1 or 2 wide, one row); charts sit between
 * and default to a square so a chart that fills its card is never cramped.
 */
export const WIDGET_KIND_SPECS: Record<WidgetKind, WidgetSizeSpec> = {
    tile: {
        default: { w: 1, h: 1 },
        presets: [
            { w: 1, h: 1 },
            { w: 2, h: 1 },
        ],
    },
    chart: {
        default: { w: 2, h: 2 },
        presets: [
            { w: 2, h: 2 },
            { w: 4, h: 2 },
            { w: 4, h: 3 },
        ],
    },
    table: {
        default: { w: 4, h: 3 },
        presets: [
            { w: 4, h: 3 },
            { w: 4, h: 4 },
        ],
    },
};

/**
 * The legacy per-kind defaults (pre KPI widget-sizing). A pin still saved at one
 * of these footprints was never deliberately resized — it took the old default —
 * so `resolveSize` re-fits it to the current spec default, which is how an
 * already-pinned widget picks up the improved sizes without resetting layouts a
 * user has actually customised. Hard-coded (not derived from the specs) so it
 * stays a stable record of what "the old default" was.
 */
const LEGACY_KIND_DEFAULTS: Record<WidgetKind, WidgetSize> = {
    tile: { w: 1, h: 1 },
    chart: { w: 2, h: 1 },
    table: { w: 4, h: 2 },
};

/**
 * Fold a widget's optional per-widget `size` override over its kind fallback.
 * The catalogue declares an override only where the archetype default is wrong
 * for that specific widget (a donut wants a square, etc.); everything else uses
 * its kind spec.
 */
export function sizeSpecFor(kind: WidgetKind, override?: WidgetSizeSpec): WidgetSizeSpec {
    return override ?? WIDGET_KIND_SPECS[kind];
}

/** The default footprint for a kind (no per-widget override). */
export function defaultSizeForKind(kind: WidgetKind): WidgetSize {
    return WIDGET_KIND_SPECS[kind].default;
}

function sizesEqual(a: WidgetSize, b: WidgetSize): boolean {
    return a.w === b.w && a.h === b.h;
}

/**
 * Step a footprint to the next preset in a spec's cycle (wrapping at the end).
 * If `current` is not one of the spec's presets (e.g. a stored size from an
 * earlier catalogue) the cycle restarts at the default. Pure — the resize
 * control (issue #216) calls this.
 */
export function cycleSize(spec: WidgetSizeSpec, current: WidgetSize): WidgetSize {
    const idx = spec.presets.findIndex((p) => sizesEqual(p, current));
    if (idx === -1) return spec.presets[0];
    return spec.presets[(idx + 1) % spec.presets.length];
}

// ─── Static Tailwind span-class maps ────────────────────────────────────────
//
// The canvas is `grid-cols-1 lg:grid-cols-4`: a single column on small screens
// (every widget full-width) and four columns from `lg` up, where the footprint
// applies. Width maps to a column span at `lg`; height maps to a row span at
// `lg` (row packing only matters once the grid is multi-column). Each value is
// a full literal class string so Tailwind's content scanner sees it.

const COL_SPAN_CLASS: Record<WidgetWidth, string> = {
    1: 'lg:col-span-1',
    2: 'lg:col-span-2',
    4: 'lg:col-span-4',
};

const ROW_SPAN_CLASS: Record<WidgetHeight, string> = {
    1: 'lg:row-span-1',
    2: 'lg:row-span-2',
    3: 'lg:row-span-3',
    4: 'lg:row-span-4',
};

/**
 * The Tailwind span classes for a footprint: a full-width base (small screens
 * stack every widget) plus the `lg` column + row spans. Returned as a single
 * space-joined string ready to drop on the grid cell's `className`.
 */
export function spanClasses(size: WidgetSize): string {
    return `col-span-1 ${COL_SPAN_CLASS[size.w]} ${ROW_SPAN_CLASS[size.h]}`;
}

// ─── Legacy coercion ────────────────────────────────────────────────────────

/**
 * A persisted pin entry. The pre-#215 wire shape was a bare id `string`; the new
 * shape is `{ id, w?, h? }` (jsonb holds both — no SQL migration). A stored
 * entry may omit `w`/`h`; those resolve to the spec default at render.
 */
export interface StoredWidgetEntry {
    id: string;
    w?: WidgetWidth;
    h?: WidgetHeight;
}

/** The legacy or current persisted entry shape, as it arrives off the wire. */
export type LegacyOrSizedEntry = string | StoredWidgetEntry;

/**
 * Normalise one persisted entry to the sized object shape. A bare id string
 * (legacy `string[]` layout) becomes `{ id }`; an object passes through. The
 * `w`/`h` it carries (if any) are preserved; a missing dimension is left
 * undefined here and resolved to the spec default at render (the kind/spec is
 * not known at this layer). Back-compatibility for existing saved layouts
 * (PRD #185) turns on this being total over both shapes.
 */
export function coerceLegacy(entry: LegacyOrSizedEntry): StoredWidgetEntry {
    if (typeof entry === 'string') return { id: entry };
    return { id: entry.id, w: entry.w, h: entry.h };
}

/**
 * Resolve a stored entry to a concrete footprint. This is the render-time
 * resolution the grid runs once it knows the widget's kind + spec:
 *
 *   - A dimension the entry OMITS falls back to the spec default.
 *   - A pin whose FULL stored footprint equals the LEGACY kind default is
 *     re-fitted to the spec default — it took the old default and was never
 *     deliberately sized, so it adopts the improved size (KPI widget-sizing).
 *   - Any other stored footprint (a size the user actually chose) is honoured.
 */
export function resolveSize(
    entry: StoredWidgetEntry,
    spec: WidgetSizeSpec,
    kind: WidgetKind,
): WidgetSize {
    const def = spec.default;
    if (entry.w !== undefined && entry.h !== undefined) {
        const legacy = LEGACY_KIND_DEFAULTS[kind];
        if (entry.w === legacy.w && entry.h === legacy.h) return def;
        return { w: entry.w, h: entry.h };
    }
    return {
        w: entry.w ?? def.w,
        h: entry.h ?? def.h,
    };
}
